/* Bulk binding edits over the real API. No `X-Lumiq-Tenant` header — hard rule 1.
 *
 * **The preview is a GET and is safe to call on render.** It writes nothing, is journalled nowhere,
 * reads no customer source, and is authorized as a *read* — the contract says why: requiring `update`
 * to see what an edit would do is how somebody ends up running the edit to find out. So the screen
 * fetches it whenever a run is selected, with no confirm step in front of it.
 *
 * **The undo is a 202 and the only mutation this screen performs.** There is deliberately no "apply"
 * control: an apply is submitted with the list of binding ids it is to change, which is a decision made
 * where the bindings are chosen — the rules screen or `lumiq-cli` — not from a run-history table. A
 * button here would need a binding picker, which is a second rules screen.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { isBulkReport, isBulkRun, isRuleBinding } from '@/shared/api/generated/rules';
import type { BulkReport, BulkRun, RuleBinding } from '@/shared/api/generated/rules';
import {
  forgetIdempotencyKey,
  getOne,
  getPage,
  idempotencyKeyFor,
  post,
} from '@/shared/api/http';

const RUNS_URL = '/v1/rules/bulk';

/** Item guards, for the reason `profiler/api` states: the envelope guard validates the envelope.
 *
 * S-71 records this as a generator defect rather than a per-screen one — `isXList` checks
 * `Array.isArray` and stops, so every slice either wraps it or ships unvalidated fields. Four do wrap
 * it now. Here the cost of not wrapping would be a run whose `state` is a string the presentation map
 * has no entry for, rendering with whatever tone `undefined` produces.
 */
function isRunList(value: unknown): value is { readonly items: readonly BulkRun[] } {
  if (typeof value !== 'object' || value === null) return false;
  const items = (value as Record<string, unknown>)['items'];
  return Array.isArray(items) && items.every(isBulkRun);
}

/** The 202 body. Accepts anything object-shaped, because the value is the status rather than the body.
 *
 * `post` requires a guard, and inventing a schema for a 202 whose contract declares only a `Location`
 * header would be asserting a shape the API does not promise. An accepted undo is a fact about the
 * response code; the run's new state is read back from the listing, which is where it belongs.
 */
function isAccepted(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object';
}

/** States a run passes through on its own, with nobody clicking anything — the worker chunks
 * through them on a schedule this screen does not control. A run sitting in one of these must be
 * polled, because "refetch on focus" only catches a reader who leaves the tab and comes back. */
const IN_FLIGHT_STATES: ReadonlySet<BulkRun['state']> = new Set([
  'pending',
  'previewing',
  'applying',
  'undoing',
]);

/** 4 seconds — close to `useBulkReport`'s own `staleTime`, so a poll and an open report panel move
 * together rather than one lagging the other by a visibly different interval. */
const RUN_POLL_INTERVAL_MS = 4 * 1000;

export function useBulkRuns(): UseQueryResult<readonly BulkRun[]> {
  return useQuery({
    queryKey: ['rules', 'bulk', 'runs'],
    queryFn: async () => (await getOne(RUNS_URL, isRunList, 'bulk runs')).items,
    /* Short, because a run in flight moves through `applying` → `applied` without the client doing
     * anything, and a stale list showing `applying` for a finished run makes the undo button look
     * broken. */
    staleTime: 5 * 1000,
    /* Polls only while a run is genuinely moving on its own. A page with nothing in flight — the
     * common case, since most runs finish in seconds — polls nothing, so refresh cost stays
     * proportional to how much is actually still changing rather than to how long the tab stays
     * open. */
    refetchInterval: (query) => {
      const runs = query.state.data ?? [];
      return runs.some((run) => IN_FLIGHT_STATES.has(run.state))
        ? RUN_POLL_INTERVAL_MS
        : false;
    },
  });
}

/** Which of the two report routes answers for a run in this state.

 * **`/preview` and `/report` share a shape and answer different questions**, and reading the wrong
 * one shipped a wrong number on the one figure that matters. The preview is a live dry-run — it
 * compares each binding's current state to the requested one — so previewing an *applied* disable
 * correctly reports every item `skipped`. This screen rendered that as the run's record, derived
 * the undo's reach from its `applied` count, and offered to "revert 0 bindings" on a run whose
 * undo restores two. Found by running the arc against the live API; no gate distinguishes two
 * routes returning one schema.
 *
 * A run that has not started has no journal, so the preview is the honest panel for it — "what
 * would change" is the only question it can answer yet. Everything past that reads the journal.
 */
export function reportSource(state: BulkRun['state']): 'preview' | 'report' {
  return state === 'pending' || state === 'previewing' ? 'preview' : 'report';
}

export function useBulkReport(
  run: Pick<BulkRun, 'run_id' | 'state'> | null,
): UseQueryResult<BulkReport | null> {
  const source = run === null ? null : reportSource(run.state);
  return useQuery({
    /* The source is in the key, so a run moving from pending to applied refetches from the journal
       rather than serving the cached dry-run under the new heading. */
    queryKey: ['rules', 'bulk', 'report', run?.run_id ?? null, source],
    enabled: run !== null,
    queryFn: async () => {
      if (run === null || source === null) return null;
      return getOne(`${RUNS_URL}/${run.run_id}/${source}`, isBulkReport, 'bulk report');
    },
    /* The run's own poll (`useBulkRuns`) is what notices a state transition; this poll is what
       keeps the open report's chunk counts moving while `run.state` sits in one in-flight value
       for several ticks — an `applying` run with 40 chunks is many `useBulkRuns` refetches before
       its state changes at all. */
    refetchInterval:
      run !== null && IN_FLIGHT_STATES.has(run.state) ? RUN_POLL_INTERVAL_MS : false,
  });
}

export function useUndoBulkRun(): UseMutationResult<void, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (runId: string) => {
      await post(
        `${RUNS_URL}/${runId}/undo`,
        /* **Keyed on the run id and memoised until it succeeds**, which is the shape hard rule 7
         * wants and the shape §9 records the evals screen getting wrong in the other direction. A
         * fresh key per click would make a retried undo a *second* undo; a key that never cleared
         * would make a deliberate re-undo a silent no-op. `forgetIdempotencyKey` on success is what
         * separates the two. */
        idempotencyKeyFor('bulk-undo', runId),
        isAccepted,
        'bulk undo',
        {},
      );
    },
    onSuccess: (_result, runId) => {
      forgetIdempotencyKey('bulk-undo', runId);
      /* Both, and the report matters as much as the list: an undo writes a second journal phase, so
       * the report a reader is looking at is now the *apply's* and the run's state has moved. Leaving
       * the report cached would show pre-undo counts beside a state saying `undone`. */
      void client.invalidateQueries({ queryKey: ['rules', 'bulk', 'runs'] });
      void client.invalidateQueries({ queryKey: ['rules', 'bulk', 'report', runId] });
    },
  });
}

/* --------------------------------------------------------------- starting a run */

export type BindingStateFilter = 'all' | 'enabled' | 'disabled';

/** The bindings a run could be built from, in the switch positions asked for.

 * **Which positions matters, and getting it wrong makes half the feature impossible.** The listing
 * defaulted to enabled-only, so a picker built on it could disable anything and enable nothing —
 * the route grew an `enabled` tri-state for exactly this. A *disable* run reads enabled bindings, an
 * *enable* run reads disabled ones, and `all` exists so the picker can show what a filter excluded.
 *
 * Capped at the route's own 500 maximum. That is below the 5,000 a run may carry, which is stated
 * on the screen rather than hidden: selecting more than 500 in one pass needs paging this listing
 * does not have, and promising a 5,000-binding selection from a 500-row read would be the
 * truncation-as-completeness defect.
 */
export const CANDIDATE_PAGE = 500;

export function useBindingCandidates(
  state: BindingStateFilter,
  enabled: boolean,
): UseQueryResult<readonly RuleBinding[]> {
  return useQuery({
    queryKey: ['rules', 'bindings', 'candidates', state],
    enabled,
    queryFn: async () =>
      (
        await getPage(
          `/v1/rules/bindings?limit=${String(CANDIDATE_PAGE)}&enabled=${state}`,
          isRuleBinding,
          'binding',
        )
      ).items,
    /* Short, because a binding disabled elsewhere while the picker is open must not still be
       offered as a candidate to disable — it would submit as a no-op the run then reports as
       skipped, which is honest but is a row the reader did not mean to include. */
    staleTime: 10 * 1000,
  });
}

export interface BulkRunRequest {
  readonly action: 'rules.binding.enable' | 'rules.binding.disable';
  readonly bindingIds: readonly string[];
  readonly chunkSize: number;
}

/** A short, stable subject for the idempotency key — **the real defect, found by submitting one**.
 *
 * The key header has a 255-character ceiling and the naive subject was every binding id joined:
 * seven UUIDs alone are 258 characters before the action, the scope or the mint even attach. Seven
 * bindings is not a stress test — it is what a single rule bound to a handful of datasets produces
 * — so this was never going to survive a real selection, only the one-id subjects every other
 * caller of `idempotencyKeyFor` happens to have.
 *
 * `requestSubject` hashes the sorted id list with FNV-1a rather than truncating it: truncating a
 * join would make two selections that differ only near the end collide on the same key, and a
 * retried submission is supposed to replay one decision, not silently merge two different ones.
 * Sorted first, so the same set of ids produces the same subject whatever order they were chosen
 * in — selection order is a UI fact, not a fact about which run this is.
 */
export function requestSubject(request: BulkRunRequest): string {
  const sorted = [...request.bindingIds].sort().join(',');
  return `${request.action}-${String(request.bindingIds.length)}-${fnv1a(sorted)}`;
}

function fnv1a(text: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

/** Starts a run. **202 and the only creating call this screen makes.**

 * The idempotency key is memoised on the *action and the selection*, so a retried submit after a
 * lost response is the same run rather than a second one over the same bindings — and changing
 * either the action or the selection mints a new key, because that is a different request. The
 * screen clears its selection on success, which is the other half: hard rule 7 stops a duplicate
 * reaching the server, and the cleared selection stops somebody re-submitting deliberately without
 * having re-chosen.
 */
export function useRequestBulkRun(): UseMutationResult<
  Record<string, unknown>,
  Error,
  BulkRunRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (request: BulkRunRequest) => {
      const subject = requestSubject(request);
      return post(
        `${RUNS_URL}/bindings`,
        idempotencyKeyFor('bulk-request', subject),
        isAccepted,
        'bulk run',
        {
          action: request.action,
          binding_ids: request.bindingIds,
          chunk_size: request.chunkSize,
        },
      );
    },
    onSuccess: (_result, request) => {
      const subject = requestSubject(request);
      forgetIdempotencyKey('bulk-request', subject);
      /* Both: the new run belongs at the top of the ledger, and the candidate listing has moved —
         the bindings just submitted will change state as the worker applies them. */
      void client.invalidateQueries({ queryKey: ['rules', 'bulk', 'runs'] });
      void client.invalidateQueries({ queryKey: ['rules', 'bindings'] });
    },
  });
}
